import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Megaphone,
  Pause,
  Play,
  RotateCcw,
  ShieldAlert,
  Square,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  useCampaignAudiencePreviewQuery,
  useCampaignDetailQuery,
  useCampaignRecipientsQuery,
} from '@/client/api/campaigns/lifecycle.query';
import {
  useCancelCampaignMutation,
  useDeleteCampaignMutation,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useStartCampaignMutation,
} from '@/client/api/campaigns/lifecycle.mutation';
import {
  CAMPAIGN_STATUS_BADGE,
  CHANNEL_LABEL,
  formatEpoch,
} from '@/client/types/campaigns';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useListFilters } from '@/client/hooks/useListFilters';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

const AccessDenied = ({ code }: { code: string }) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Requires <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{code}</code>.
    </p>
  </div>
);

const CampaignDetailPage = () => {
  const { cid = '' } = useParams<{ cid: string }>();
  const navigate = useNavigate();

  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/campaigns' },
    { label: 'Detail' },
  ]);

  const viewPerm = useHasPermission('campaign.view');
  const createPerm = useHasPermission('campaign.create');
  const sendPerm = useHasPermission('campaign.send');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canCreate = createPerm.isSuperAdmin || createPerm.hasPermission;
  const canSend = sendPerm.isSuperAdmin || sendPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [scheduleAt, setScheduleAt] = useState('');
  const { data: campaign, isLoading, isError } = useCampaignDetailQuery(
    cid,
    canView && !permLoading,
  );
  const audienceQ = useCampaignAudiencePreviewQuery(
    cid,
    canView && !permLoading && !!campaign && campaign.status === 'draft',
  );

  const {
    filters,
    page,
    limit,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
  } = useListFilters<{ status: string }>({
    initialFilters: { status: '' },
    initialLimit: 25,
  });

  const recipientsParams = useMemo(
    () => ({
      limit,
      offset: (page - 1) * limit,
      ...(filters.status ? { status: filters.status } : {}),
    }),
    [filters.status, page, limit],
  );

  const recipientsQ = useCampaignRecipientsQuery(
    cid,
    recipientsParams,
    canView && !permLoading && !!campaign && campaign.status !== 'draft',
  );

  const start = useStartCampaignMutation();
  const pause = usePauseCampaignMutation();
  const resume = useResumeCampaignMutation();
  const cancel = useCancelCampaignMutation();
  const del = useDeleteCampaignMutation();

  const [confirmGaps, setConfirmGaps] = useState(false);
  const mergeFields = Object.entries(audienceQ.data?.merge?.fields ?? {});
  const gaps = audienceQ.data?.merge?.blocking ?? [];

  const busy =
    start.isPending || pause.isPending || resume.isPending || cancel.isPending || del.isPending;

  const run = async (
    label: string,
    fn: () => Promise<unknown>,
  ) => {
    try {
      await fn();
      toast.success(label, { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Action failed', { richColors: true });
    }
  };

  if (permLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied code="campaign.view" />
        </div>
      </div>
    );
  }

  if (isError || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl rounded-xl border border-red-100 bg-white p-8 text-center text-sm text-red-600">
          Campaign not found.
          <div className="mt-4">
            <Button asChild variant="outline">
              <Link to="/admin/relationship/campaigns">Back to list</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const p = campaign.progress;
  const status = campaign.status;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Link
              to="/admin/relationship/campaigns"
              className="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              All campaigns
            </Link>
            <div className="mb-1 flex flex-wrap items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <Megaphone className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">{campaign.name}</h1>
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  CAMPAIGN_STATUS_BADGE[status] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {status}
              </span>
            </div>
            <p className="font-mono text-xs text-gray-400">{campaign.campaign_id}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {status === 'draft' && canCreate && (
              <Button asChild variant="outline">
                <Link to={`/admin/relationship/campaigns/${cid}/edit`}>Edit draft</Link>
              </Button>
            )}
            {status === 'draft' && canSend && (
              <>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />
                <Button
                  disabled={busy}
                  className="gap-2"
                  onClick={() =>
                    gaps.length > 0
                      ? setConfirmGaps(true)
                      : run('Campaign started', async () => {
                      const body = scheduleAt
                        ? { send_at: Math.floor(new Date(scheduleAt).getTime() / 1000) }
                        : {};
                      const result = await start.mutateAsync({
                        cid,
                        body,
                        idempotencyKey: generateIdempotencyKey(),
                      });
                      if (result.override_denied) {
                        toast.warning('Override denied — prefs will be respected', {
                          richColors: true,
                        });
                      }
                    })
                  }
                >
                  {start.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {scheduleAt ? 'Schedule' : 'Start now'}
                </Button>
              </>
            )}
            {status === 'running' && canSend && (
              <Button
                disabled={busy}
                variant="outline"
                className="gap-2"
                onClick={() =>
                  run('Paused', () =>
                    pause.mutateAsync({ cid, idempotencyKey: generateIdempotencyKey() }),
                  )
                }
              >
                <Pause className="h-4 w-4" />
                Pause
              </Button>
            )}
            {status === 'paused' && canSend && (
              <Button
                disabled={busy}
                className="gap-2"
                onClick={() =>
                  run('Resumed', () =>
                    resume.mutateAsync({ cid, idempotencyKey: generateIdempotencyKey() }),
                  )
                }
              >
                <RotateCcw className="h-4 w-4" />
                Resume
              </Button>
            )}
            {['draft', 'scheduled', 'running', 'paused'].includes(status) && canSend && status !== 'draft' && (
              <Button
                disabled={busy}
                variant="outline"
                className="gap-2 text-orange-700"
                onClick={() =>
                  run('Canceled', () =>
                    cancel.mutateAsync({ cid, idempotencyKey: generateIdempotencyKey() }),
                  )
                }
              >
                <Square className="h-4 w-4" />
                Cancel
              </Button>
            )}
            {status === 'draft' && canCreate && (
              <Button
                disabled={busy}
                variant="outline"
                className="gap-2 text-red-600"
                onClick={() => {
                  if (!window.confirm('Delete this draft?')) return;
                  run('Deleted', async () => {
                    await del.mutateAsync({ cid, idempotencyKey: generateIdempotencyKey() });
                    navigate('/admin/relationship/campaigns', { replace: true });
                  });
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total', value: p?.total ?? '—' },
            { label: 'Sent', value: p?.sent ?? '—' },
            { label: 'Failed', value: p?.failed ?? '—' },
            { label: 'Opted out', value: p?.opted_out ?? '—' },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
            >
              <p className="text-xs text-gray-500">{card.label}</p>
              <p className="mt-1 text-2xl font-black text-gray-900">{card.value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Details</h3>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs text-gray-500">Channels</dt>
                <dd>
                  {(campaign.channels || [])
                    .map((ch) => CHANNEL_LABEL[ch] || ch)
                    .join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Respect prefs</dt>
                <dd>{campaign.respect_prefs === false ? 'No (override)' : 'Yes'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Started</dt>
                <dd>{formatEpoch(campaign.started_at)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Finished</dt>
                <dd>{formatEpoch(campaign.finished_at)}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs text-gray-500">Subject</dt>
                <dd>{campaign.content?.subject || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs text-gray-500">Body</dt>
                <dd className="whitespace-pre-wrap rounded-md bg-gray-50 p-3">
                  {campaign.content?.body || '—'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Metrics by channel</h3>
            {campaign.metrics_by_channel &&
            Object.keys(campaign.metrics_by_channel).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(campaign.metrics_by_channel).map(([ch, m]) => (
                  <div key={ch} className="rounded-md border border-gray-100 p-3 text-sm">
                    <p className="font-medium">{CHANNEL_LABEL[ch] || ch}</p>
                    <p className="text-gray-600">
                      {m?.sent ?? 0} sent · {m?.failed ?? 0} failed · {m?.opted_out ?? 0} opted out
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No channel metrics yet.</p>
            )}

            {status === 'draft' && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                  Audience preview
                </h4>
                {audienceQ.isLoading && <Skeleton className="h-16 w-full" />}
                {audienceQ.data && (
                  <p className="text-sm text-gray-700">
                    ~{audienceQ.data.estimated_size?.toLocaleString() ?? '—'} recipients
                  </p>
                )}
                {mergeFields.length > 0 && (
                  <div className="mt-3">
                    <h5 className="mb-1.5 text-xs font-medium text-gray-500">Placeholders in this campaign</h5>
                    <ul className="space-y-1 text-sm">
                      {mergeFields.map(([key, f]) => (
                        <li key={key} className="flex flex-wrap items-center gap-x-2">
                          <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{`{{${key}}}`}</code>
                          {f.missing === 0 ? (
                            <span className="text-emerald-700">everyone has it</span>
                          ) : f.blocking ? (
                            <span className="text-red-700">
                              missing for {f.missing.toLocaleString()} of {f.total.toLocaleString()}, no fallback
                            </span>
                          ) : (
                            <span className="text-gray-600">
                              missing for {f.missing.toLocaleString()}, they get &ldquo;{f.fallback}&rdquo;
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {gaps.length > 0 && (
                      <p className="mt-2 text-xs text-red-700">
                        Add a fallback such as <code>{'{{company|your business}}'}</code>, narrow the audience, or
                        start anyway and those people see a blank.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {status !== 'draft' && (
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">Recipients</h3>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
              >
                <option value="">All</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="opted_out">Opted out</option>
                <option value="suppressed">Suppressed</option>
                <option value="pending">Pending</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>
            {recipientsQ.isLoading && (
              <div className="space-y-2 p-4">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            )}
            {!recipientsQ.isLoading && (recipientsQ.data?.recipients?.length ?? 0) === 0 && (
              <p className="p-6 text-center text-sm text-gray-500">No recipients yet.</p>
            )}
            {(recipientsQ.data?.recipients?.length ?? 0) > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">UID</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Phone</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recipientsQ.data!.recipients!.map((r, i) => (
                      <tr key={`${r.uid}-${r.email}-${i}`}>
                        <td className="px-4 py-3">{r.uid ?? '—'}</td>
                        <td className="px-4 py-3">{r.email || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{r.phone || '—'}</td>
                        <td className="px-4 py-3">{r.status || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {recipientsQ.data?.pagination && (
              <div className="border-t border-gray-100 px-4 py-3">
                <Pagination
                  total={recipientsQ.data.pagination.total ?? 0}
                  page={page}
                  itemsPerPage={limit}
                  limit={limit}
                  setPage={handlePageChange}
                  setItemsPerPage={handleLimitChange}
                  showButtons
                  showSelect
                />
              </div>
            )}
          </div>
        )}
      </div>
      <AlertDialog open={confirmGaps} onOpenChange={setConfirmGaps}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Some people lack placeholder data</AlertDialogTitle>
            <AlertDialogDescription>
              {gaps
                .map((k) => `{{${k}}} is missing for ${audienceQ.data?.merge?.fields[k]?.missing ?? '?'}`)
                .join('; ')}
              . Those people will see a blank where it appears. Start anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                run('Campaign started', async () => {
                  setConfirmGaps(false);
                  await start.mutateAsync({
                    cid,
                    body: {
                      allow_missing_fields: true,
                      ...(scheduleAt ? { send_at: Math.floor(new Date(scheduleAt).getTime() / 1000) } : {}),
                    },
                    idempotencyKey: generateIdempotencyKey(),
                  });
                })
              }
            >
              Start anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignDetailPage;
